import { Injector } from '@angular/core';
import { ethers } from 'ethers';

import { parseToFirstNonZero } from '../../utils';
import { ICurrency } from '../base-currency';
import { ITransactionFee, ITransactionParsed, ITransactionResponceEx } from '../explorers/types';
import { IParseService } from './base-parse';

export class EVMParserService extends IParseService
{
    constructor(protected injector: Injector)
    {
        super(injector);
    }

    /*transaction-confirmation */
    public async parseDataConfirmation(_tx, _chainId: number, _currency: ICurrency)
    {
        if (!_currency.getIsToken())
        {
            return this.parseTransaction(_tx, _currency);
        }

        if (!!_tx.data)
        {
            let formatValue, valuePrice;
            const abi = this.contractFactory.loadContractABI();
            const parsedTxData = await this.parseConfirmationData(_tx, abi as string, _chainId);
            if (parsedTxData.value != undefined)
            {
                formatValue = Number(parsedTxData.value);
                valuePrice = Number(parsedTxData.price);
            }

            return {
                ..._tx,
                signature: parsedTxData.signature,
                symbol: parsedTxData.symbol,
                name: parsedTxData.name,
                to: parsedTxData.to,
                amount: {
                    value: parsedTxData.value,
                    price: valuePrice,
                    formatValue: formatValue,
                },
                // fee: feeData
                contract: parsedTxData.contract
            };
        }
        else
        {
            return this.parseTransaction(_tx, _currency);
        }
    }

    public async parseConfirmationData(_txData: ITransactionResponceEx, _abi: string, _chainId: number): Promise<any>
    {
        if (_abi != null)
        {
            let contractInterface, data;

            try
            {
                try
                {
                    contractInterface = new ethers.Interface(_abi);
                    data = contractInterface.parseTransaction(<any>_txData);
                }
                catch (e)
                {
                    console.debug(e);

                    const contractAddress = _txData.to;
                    const explorer = await this.explorerService.getExplorer(_chainId);
                    const contractAbi = await explorer.GetContractABI(contractAddress);

                    contractInterface = new ethers.Interface(contractAbi);
                    data = contractInterface.parseTransaction(<any>_txData);
                }
            }
            catch (e)
            {
                console.debug(e);
            }

            if (!!data)
            {
                let tx: any = { ..._txData };
                let contractInstance;

                tx.value =
                    (data.args.amountIn || data.args.amountInMax) ||
                    data.args.tokens ||
                    data.args.value ||
                    data.args.amount ||
                    data.args._value ||
                    data.args._amount ||
                    data.value ||
                    _txData.value;

                if (Number(_txData.value) > 0)
                { }
                else if (!!data.name.startsWith('approve'))
                {
                    tx.contractAddress = _txData.to;

                    const { contract, symbol, name } = await this.getTokenContract(_chainId, _txData.to);

                    contractInstance = contract;
                    tx.to = data.args._spender;
                    tx.signature = 'approve';
                    tx.symbol = symbol;
                    tx.name = name;
                }
                else if (!!data.args?.path?.slice(0, 1))
                {
                    tx.contractAddress = data.args.path[ 0 ];
                    tx.to = data.args.path[ 0 ];
                    const { contract, symbol, name } = await this.getTokenContract(_chainId, data.args?.path[ 0 ]);

                    contractInstance = contract;
                    tx.symbol = symbol;
                    tx.name = name;
                }
                else
                {
                    const contract = await this.getTokenContract(_chainId, _txData.to);
                    if (!!contract && contract.symbol)
                    {
                        tx.contractAddress = _txData.to;
                        contractInstance = contract.contract;
                        tx.to = data.args._to;
                        tx.symbol = contract.symbol;
                        tx.name = contract.name;
                    }
                }

                const price = 0;
                if (!!contractInstance && !!contractInstance.decimals && !!(tx.symbol))
                {
                    const formatValue = await this.formatContractValue(contractInstance, price, tx.value);

                    if (formatValue.done)
                    {
                        tx.price = formatValue.price;
                        tx.value = formatValue.value;
                    }
                }
                else
                {
                    tx.value = ethers.formatEther(tx.value);
                    tx.price = Number(tx.value) * Number(price);
                }

                return { ...tx, contract: { contractInstance, symbol: tx.symbol, name: tx.name, contractAddress: tx.contractAddress } };
            }
        }
        return _txData;
    }

    public async parseFeeData(_tx, _currency)
    {
        const price = (await this.pricesService.getPrice(_currency.getFeeSymbol(), 'usd', _currency.getChainId())).price;
        const gasPrice = _tx.gasPrice || _tx.maxFeePerGas;

        if (gasPrice)
        {
            let gasUsed = _tx.gasUsed ?? _tx.gasLimit;

            if (!gasUsed)
            {
                delete _tx.signature;
                gasUsed = (await _currency.populateTransaction(_tx, false)).gasLimit;
            }

            if (typeof gasUsed !== 'bigint') gasUsed = BigInt(gasUsed);

            let feeBN = BigInt(gasUsed) * BigInt(gasPrice);
            let feeString = parseToFirstNonZero(ethers.formatEther(feeBN), 4);

            const feeAmount = ethers.formatUnits(gasPrice, 'gwei');
            const fiatFee = parseToFirstNonZero(Number(price) * Number(feeString), 2);
            return {
                feeAmount: feeAmount,
                fee: feeString,
                format: 'gwei',
                fiatFee: fiatFee,
            };
        }
    }

    public async getContractByTxData(_tx: any, _chainId: number, _address: string)
    {
        let contractInterface, data;

        let abi = this.contractFactory.loadContractABI();

        try
        {
            contractInterface = new ethers.Interface(<string>abi);
            data = contractInterface.parseTransaction(<any>_tx);
        }
        catch (e)
        {
            console.debug(e);

            const contractAddress = _tx.to;
            const explorer = await this.explorerService.getExplorer(_chainId);
            const contractAbi = await explorer.GetContractABI(contractAddress);

            contractInterface = new ethers.Interface(contractAbi);
            data = contractInterface.parseTransaction(<any>_tx);
        }
        console.log(contractInterface);
        console.log(data);

    }

    public async getTokenContract(_chainId: number, _address: string)
    {
        try
        {
            const contractToken = await this.contractFactory.create(
                _address,
                _chainId
            );
            return contractToken;
        }
        catch (e)
        {
            console.debug(e);
        }
    }

    /**
     * parse for additional information
     * @param _tx
     * @param _currency
     * @param _parseData
     */
    public async parseDataVisualization(_tx: ITransactionResponceEx, _currency: ICurrency, _parseData: boolean = true)
    {
        if (_currency.getIsToken())
        {
            return await this.parseTransactionContract(_tx, _currency, _parseData);
        }

        return await this.parseTransaction(_tx, _currency);
    }

    public async parseTransaction(_tx: ITransactionResponceEx, _currency: ICurrency)
    {
        if (_tx.value as any === '0x')
        {
            _tx.value = '0' as any;
        }

        const value = ethers.formatEther(_tx.value || '0');
        const price = (await this.pricesService.getPrice(_currency.getFeeSymbol(), 'usd', _currency.getChainId())).price;
        const valuePrice = Number(value) * Number(price);

        let feeData: ITransactionFee = await <any>this.parseFeeData(_tx, _currency);

        return {
            ..._tx,
            amount: {
                value: ethers.formatEther(_tx.value || '0'),
                price: valuePrice,
            },
            fee: feeData,
        };
    }

    public async parseTransactionContract(_tx: ITransactionResponceEx, _currency: ICurrency, _parseData: boolean = true): Promise<ITransactionParsed>
    {
        const tx = { ..._tx };
        let parsedTxData;

        if (!!tx.data && _parseData)
        {
            const abi = _currency.contractAbi || (await _currency.getAbiErc());
            parsedTxData = await this.parseTxData(tx, abi as string, _currency.getChainId(), _currency);
            tx.value = parsedTxData.value;
            tx.to = parsedTxData.to;
            tx.from = parsedTxData.from || tx.from;
            tx.signature = parsedTxData.signature;
        }

        const txRes = await this.parseTransaction(tx, _currency);

        let contract = _currency.getContractInstance();

        if (!!contract && !!contract.decimals && !!_currency.getSymbol())
        {
            let price;
            if ([ 'USDT', 'BUSD' ].includes(_currency.getSymbol()))
            {
                price = null;
            }
            else
            {
                price = (
                    await this.pricesService.getPrice(_currency.getSymbol(), 'usd', _currency.getChainId(), _currency.getAddress())
                ).price;
            }

            const formatValue = await this.formatContractValue(contract, price, tx.value);

            if (formatValue.done)
            {
                txRes.amount.price = formatValue.price;
                txRes.amount.value = parsedTxData?.amount || formatValue.value;
            }
        }

        return txRes;
    }

    public async formatContractValue(contract, _price, _value)
    {
        const txRes: any = {};

        if (!!contract && !!contract.decimals)
        {
            let decimals = 18;

            try
            {
                decimals = await contract.decimals();
            }
            catch (err)
            {
                console.error(err);
            }

            txRes.value = ethers.formatUnits(_value || '0', decimals);

            const valuePrice = Number(txRes.value) * Number(_price);

            txRes.price = valuePrice;
            txRes.done = true;
        }

        return txRes;
    }

    public async parseTxData(_txData: ITransactionResponceEx, _abi: string, _chainId: number, _currency: ICurrency): Promise<any>
    {
        if (_txData.data === 'deprecated') return _txData;

        if (_abi != null)
        {
            let contractInterface, data;

            data = await this.parseLogsData(_txData, _abi, _chainId, _currency);

            if (!data)
            {
                try
                {
                    try
                    {
                        contractInterface = new ethers.Interface(_abi);
                        data = contractInterface.parseTransaction(<any>_txData);
                    }
                    catch (e)
                    {
                        console.debug(e);

                        const contractAddress = _txData.to;
                        const explorer = await this.explorerService.getExplorer(_chainId);
                        const contractAbi = await explorer.GetContractABI(contractAddress);

                        contractInterface = new ethers.Interface(contractAbi);
                        data = contractInterface.parseTransaction(<any>_txData);
                    }
                }
                catch (e)
                {
                    console.debug(e);
                }
            }

            if (!!data)
            {
                let tx: any = {};

                if (!!data.name.startsWith('transfer')) tx.signature = 'transfer';

                tx.value =
                    (_currency.getTokenAddress()?.toLowerCase() === data.args.path?.slice(-1)[ 0 ].toLowerCase() ? (data.args.amountOut || data.args.amountOutMin) : (data.args.amountIn || data.args.amountInMax)) ||
                    data.args.tokens ||
                    data.args.value ||
                    data.args.amount ||
                    data.args._value ||
                    data.args._amount ||
                    _txData.value;



                tx.from = data.args.from ?? data.args._from ?? _txData.from;
                tx.to = data.args.to ?? data.args._to ?? data.args.recipient ?? _txData.to;

                if (tx.value !== _txData.value)
                {
                    return tx;
                }
            }
        }

        if (!!_txData.hash)
        {
            const tx = await this.getFromHistoryDataTx(_txData.hash, _currency);
            console.log(tx);
            if (tx)
            {
                return tx;
            }
        }
        return _txData;
    }

    public async parseLogsData(_txData: ITransactionResponceEx, _abi: string, _chainId: number, _currency: ICurrency): Promise<any>
    {
        try
        {
            const explorer = await this.explorerService.getExplorer(_chainId);
            const provider = await this.providerFactoryService.getProvider(_currency.getChainId());
            const tx_receipt = await <any>provider.getTransactionReceipt(_txData.hash);

            if (!!tx_receipt?.logs)
            {
                for (let oneLog of tx_receipt.logs)
                {
                    if (oneLog.address?.toUpperCase() !== _currency.getTokenAddress()?.toUpperCase())
                    {
                        continue;
                    }

                    const contractAddress = oneLog.address;
                    const contractAbi = await explorer.GetContractABI(contractAddress);
                    let contractInterface = new ethers.Interface(contractAbi);
                    const data = contractInterface.parseLog(oneLog);

                    if (data.name.toUpperCase() === 'TRANSFER')
                    {
                        return data;
                    }
                }
            }
        }
        catch
        {

        }
    }

    public async parseDataValueAndFee(_tx, _currency: ICurrency): Promise<any>
    {
        let value = ethers.formatEther(_tx.value || '0');

        let feeData = {};

        const gasPrice = _tx.gasPrice || _tx.maxFeePerGas;

        if (gasPrice)
        {
            let gasUsed = _tx.gasUsed ?? _tx.gasLimit;

            if (typeof gasUsed !== 'bigint') gasUsed = BigInt(gasUsed);

            let feeBN = BigInt(gasUsed) * BigInt(gasPrice);
            let feeString = parseToFirstNonZero(ethers.formatEther(feeBN), 4);

            feeData = {
                fee: feeString,
                format: 'gwei',
            };
        }

        if (_currency.getIsToken())
        {
            let contract = _currency.getContractInstance();

            if (!!contract && !!contract.decimals && !!_currency.getSymbol())
            {
                let decimals = 18;

                try
                {
                    decimals = await contract.decimals();
                }
                catch (err)
                {
                    console.error(err);
                }

                value = ethers.formatUnits(_tx.value || '0', decimals);
            }
        }

        if (_currency.getIsToken() && _tx.amount && Number(value) === 0)
        {
            value = _tx.amount;
        }

        return {
            ..._tx,
            amount: {
                value: value,
            },
            fee: feeData,
        };
    }

    private async getFromHistoryDataTx(_hash: string, _currency: ICurrency, _resultData: string = '')
    {
        let history = await _currency.getHistory();

        return history.find(v => v.hash === _hash);
    }
}
